import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Typography, Button, Space, Descriptions, Row, Col, Tag, Spin, Tabs, message } from 'antd';
import { EditOutlined, DeleteOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getParkingById, deleteParking, updateParking } from '@/api/properties';
import { Parking } from '@/types/resources/property';
import { useAuth } from '@/contexts/AuthContext';
import ActivityLogList from '@/components/common/ActivityLogList';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';
import { ParkingFormModal } from '@/components/features/resources/properties/ParkingFormModal';

const { Title } = Typography;

export const ParkingDetails: React.FC = () => {
  const { t } = useTranslation(['properties', 'common']);
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[] } | undefined>(undefined);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const state = location.state as { parking: Parking } | null;

  // Get parking data
  const { data: parkingSpot, isLoading } = useQuery({
    queryKey: ['parking', id, firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getParkingById(id!, firestoreUser);
    },
    enabled: !!id && !!firestoreUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Use state data if available, otherwise use fetched data
  const displayParking = state?.parking || parkingSpot;

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Parking> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateParking(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:parkings.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['parkings', id] });
      setIsModalVisible(false);
    },
    onError: (error: Error) => {
      message.error(t('properties:parkings.messages.updateError'));
      console.error('Error updating parking:', error);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteParking(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:parkings.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['parkings'] });
      navigate('/resources/properties/parkings');
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error as any);
      } else {
        message.error(t('properties:parkings.messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting parking:', error);
    }
  });

  const handleEdit = () => {
    setIsModalVisible(true);
  };

  const handleModalSubmit = (values: Partial<Parking>) => {
    if (displayParking?.id) {
      updateMutation.mutate({ id: displayParking.id, data: values });
    }
  };

  const handleDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
      setShowDeleteModal(false);
    }
  };

  if (isLoading && !state?.parking) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!displayParking) {
    return <Card>Parking spot not found</Card>;
  }

  const tabItems = [
    {
      key: 'overview',
      label: t('common:details'),
      children: (
        <Card>
          <Descriptions bordered column={1}>
            <Descriptions.Item label={t('properties:fields.number')}>
              {displayParking.number}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.block')}>
              {displayParking.block?.name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.type')}>
              {t(`properties:parkingTypes.${displayParking.type}`)}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.status')}>
              <Tag color={displayParking.status === 'occupied' ? 'green' : displayParking.status === 'maintenance' ? 'orange' : 'blue'}>
                {t(`common:status.${displayParking.status}`)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.apartmentUnit')}>
              {displayParking.apartmentUnit?.number || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.pricing')}>
              {displayParking.pricing ? (
                <div>
                  {displayParking.pricing.amount} {displayParking.pricing.currency} / {displayParking.pricing.period}
                </div>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('properties:fields.description')}>
              {displayParking.description || '-'}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ),
    },
    {
      key: 'activityLog',
      label: t('common:activityLog'),
      children: (
        <Card>
          <ActivityLogList activityRecords={Array.isArray(displayParking.activityRecords) ? displayParking.activityRecords : []} />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ width: '100%' }}>
      {/* Header */}
      <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/resources/properties/parkings')}>
              {t('common:back')}
            </Button>
            <Title level={3} style={{ margin: 0 }}>{displayParking.number}</Title>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Button icon={<EditOutlined />} onClick={handleEdit}>
              {t('common:actions.edit')}
            </Button>
            <Button 
              danger 
              icon={<DeleteOutlined />} 
              onClick={() => setShowDeleteModal(true)}
              loading={deleteMutation.isPending}
            >
              {t('common:actions.delete')}
            </Button>
          </Space>
        </Col>
      </Row>

      {/* Content */}
      <Tabs items={tabItems} />

      <ParkingFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleModalSubmit}
        parking={displayParking}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={handleDelete}
        title={t('properties:parkings.messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
      />
    </div>
  );
}; 